import dedent from 'ts-dedent'
import {
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentOutputType,
  WorkflowComponentParamType
} from '../types'
import { WorkflowComponent } from './common/workflowcomponent'
import { tempTableName } from '../utils'

class JoinExtended extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'alteryx.join',
        group: 'Joins',
        title: 'Join (extended)',
        description: `This component combines two input tables based on common fields between the two tables or based on record position.`,
        tags: ['alteryx']
      },
      [
        {
          name: 'lefttable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Left table'
        },
        {
          name: 'righttable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Right table'
        },
        {
          name: 'joinby',
          type: WorkflowComponentParamType.Selection,
          title: 'Join By',
          options: ['Record Position', 'Specific Field']
        },
        {
          name: 'leftcol',
          type: WorkflowComponentParamType.Column,
          title: 'Column in Left Table',
          parent: 'lefttable',
          optional: true,
          showIf: [
            {
              parameter: 'joinby',
              value: 'Specific Field'
            }
          ]
        },
        {
          name: 'rightcol',
          type: WorkflowComponentParamType.Column,
          title: 'Column in Right Table',
          parent: 'righttable',
          optional: true,
          showIf: [
            {
              parameter: 'joinby',
              value: 'Specific Field'
            }
          ]
        }
      ],
      [
        {
          name: 'left',
          title: 'Left',
          description: 'Table with unjoined rows from left table',
          type: WorkflowComponentOutputType.Table
        },
        {
          name: 'right',
          title: 'Right',
          description: 'Table with unjoined rows from right table',
          type: WorkflowComponentOutputType.Table
        },
        {
          name: 'join',
          title: 'Join',
          description: 'Joined table',
          type: WorkflowComponentOutputType.Table
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    if (connection.type !== Provider.BigQuery) {
      return null
    }
    const tablenameLeft = connection.getOutputTableName(workflow.id, componentName, params, 'left', codeType)
    const tablenameRight = connection.getOutputTableName(workflow.id, componentName, params, 'right', codeType)
    const tablenameJoined = connection.getOutputTableName(workflow.id, componentName, params, 'join', codeType)
    const preparedParams = this._prepareParameters(params)
    let selectLeft
    let selectRight
    let selectJoined
    if (preparedParams.joinby === 'Record Position') {
      selectJoined = `WITH a AS (
            SELECT ROW_NUMBER() OVER() _rowna, *
            FROM \`${preparedParams.lefttable}\`
            ),
            b AS (
            SELECT ROW_NUMBER() OVER() _rownb, *
            FROM \`${preparedParams.righttable}\`
            )
            SELECT a.* EXCEPT(_rowna), %s
            FROM a
            JOIN b
            ON a._rowna = b._rownb`
      selectLeft = `WITH a AS (
            SELECT ROW_NUMBER() OVER() _rown, *
            FROM \`${preparedParams.lefttable}\`
            )
            SELECT a.* EXCEPT (_rown)
            FROM a, \`${preparedParams.lefttable}\` b
            WHERE _rown > (SELECT COUNT(*) FROM \`${preparedParams.righttable}\`)`
      selectRight = `WITH a AS (
            SELECT ROW_NUMBER() OVER() _rown, *
            FROM \`${preparedParams.righttable}\`
            )
            SELECT a.* EXCEPT (_rown)
            FROM a, \`${preparedParams.righttable}\`
            WHERE _rown > (SELECT COUNT(*) FROM \`${preparedParams.lefttable}\`)`
    } else {
      selectJoined = `SELECT a.*, %s
            FROM \`${preparedParams.lefttable}\` a
            INNER JOIN \`${preparedParams.righttable}\` b
            ON a.${preparedParams.leftcol} = b.${preparedParams.rightcol}`
      selectLeft = `SELECT *
            FROM \`${preparedParams.lefttable}\`
            WHERE NOT EXISTS (
              SELECT 1
              FROM \`${preparedParams.righttable}\`
              WHERE \`${preparedParams.lefttable}\`.${preparedParams.leftcol} = \`${preparedParams.righttable}\`.${preparedParams.rightcol}
            )`
      selectRight = `SELECT *
            FROM \`${preparedParams.righttable}\`
            WHERE NOT EXISTS (
              SELECT 1
              FROM \`${preparedParams.lefttable}\`
              WHERE \`${preparedParams.lefttable}\`.${preparedParams.leftcol} = \`${preparedParams.righttable}\`.${preparedParams.rightcol}
            )`
    }
    const queryLeft = connection.getCreateTempTableQuery(tablenameLeft, selectLeft, codeType, null)
    const queryRight = connection.getCreateTempTableQuery(tablenameRight, selectRight, codeType, null)
    const createTableJoined = connection.getCreateTempTableQuery(tablenameJoined, selectJoined, codeType, null)
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    const tokens = (preparedParams.righttable as string)?.split('.')
    const datasetname = storedProc ? connection.tempLocation : `${tokens[0]}.${tokens[1]}`
    const tablename = storedProc ? `${tempTableName(componentName, preparedParams.righttable)}` : tokens[2]
    const storedProcTable = connection.quoteName(`${connection.tempLocation}.${tablename}`)
    const queryJoined = dedent`DECLARE alias STRING;
                          ${
                            storedProc
                              ? dedent`
                                  CREATE TABLE ${storedProcTable} AS
                                  SELECT * FROM ${connection.quoteName(preparedParams.righttable as string)}
                                  WHERE 1=0;`
                              : ''
                          }
                          EXECUTE IMMEDIATE
                          '''
                            with __alias AS(
                              SELECT CONCAT(
                                'b.', column_name, ' AS ', column_name, '_joined'
                              ) col_alias
                              FROM \`${datasetname}\`.INFORMATION_SCHEMA.COLUMNS
                            WHERE table_name = '${tablename}'
                            )
                            SELECT STRING_AGG(col_alias, ', ')
                            FROM __alias
                          '''
                          INTO alias;
                          ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                          ${dedent(
                            connection.executeDynamicSQL({
                              sql: createTableJoined,
                              addQuotes: true,
                              replacements: [["'%s'", 'alias']]
                            })
                          )}`
    const query = `${queryJoined}\n${queryLeft}\n${queryRight}`
    const sqlret: QueryAndOutputs = { query: query, outputs: {} }
    sqlret.outputs['left'] = tablenameLeft
    sqlret.outputs['right'] = tablenameRight
    sqlret.outputs['join'] = tablenameJoined
    return sqlret
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery]
  }
}

export { JoinExtended }
